import { useContext } from "react";
import { Accordion, Card, Table } from "react-bootstrap";
import type { AccountRecord } from "../lib/explainTypes";
import { RebalanceContext } from "../../RebalanceInfo";
import { hasRebalanceMetrics } from "../../nullRebalanceMetrics";
import RebalanceAccountInsights, {
  type RebalanceAccountWithMetrics,
} from "../../RebalanceAccountInsights";
import SummaryRow from "../summary/SummaryRow";
import CashSection from "./CashSection";
import ConstrainSection from "./ConstrainSection";
import RestrictionsSection from "./RestrictionsSection";
import SubstitutionsSection from "./SubstitutionsSection";
import TradesSection from "./TradesSection";

interface Props {
  account: AccountRecord;
}

// Reads `accounts[i]`. Each sub-section consumes only its slice
// (architecture spec §12 rule 5). Sub-sections render in algorithm
// order and are individually collapsible.
//
// The legacy `RebalanceAccountInsights` widget (allocation/risk
// charts + cash/sell/buy/net summary) is mounted above the
// algorithm-stage accordion. It does its own data fetching via
// `RebalanceContext.rebalance.accounts[i]` rather than the v3
// payload because those metrics are computed by the rebalance
// pipeline and aren't part of the explain spec.
export default function AccountBlock({ account }: Props) {
  const { rebalance, household } = useContext(RebalanceContext);
  const rebalanceAccount = rebalance?.accounts?.find(
    (a) => a.id === account.account_id,
  );

  return (
    <Card className="mb-3" id={`account-${account.account_id}`}>
      <Card.Body>
        {hasRebalanceMetrics(rebalanceAccount) && (
          <div className="mb-4">
            <RebalanceAccountInsights
              rebalanceAccount={rebalanceAccount as RebalanceAccountWithMetrics}
              rebalanceType={rebalance?.type || "tolerance"}
              householdModelId={household?.assignedModelId}
              modelAssignmentStrategy={household?.modelAssignmentStrategy}
              showCalculatedTargetForResidual
            />
          </div>
        )}

        {/* Repeat this account's Summary row inline so the matrix stays
            visible while reading the account-stage detail below -- saves
            scrolling back up for multi-account households. */}
        <Table size="sm" responsive className="mb-4">
          <tbody>
            <SummaryRow account={account} />
          </tbody>
        </Table>

        <Accordion defaultActiveKey={["cash"]} alwaysOpen>
          <Accordion.Item eventKey="cash">
            <Accordion.Header>Cash</Accordion.Header>
            <Accordion.Body>
              <CashSection
                cash={account.cash_adjustment}
                accountId={account.account_id}
                cashBalanceOverride={account.cash_balance_override ?? null}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="restrictions">
            <Accordion.Header>Restrictions</Accordion.Header>
            <Accordion.Body>
              <RestrictionsSection
                restrictions={account.restriction_adjustment}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="substitutions">
            <Accordion.Header>Substitutions</Accordion.Header>
            <Accordion.Body>
              <SubstitutionsSection secondary={account.secondary_securities} />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="constrain">
            <Accordion.Header>Constrain</Accordion.Header>
            <Accordion.Body>
              <ConstrainSection
                constrained={account.constrained_allocation}
                taxConstraints={account.tax_constraints}
                isTaxable={account.is_taxable}
                modelCheckpoint={account.checkpoints.model}
                restrictions={account.restriction_adjustment}
                secondary={account.secondary_securities}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="trades">
            <Accordion.Header>Trades</Accordion.Header>
            <Accordion.Body>
              <TradesSection
                trades={account.trade_generation}
                isTaxable={account.is_taxable}
                accountId={account.account_id}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
